import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { getCardFormDataPath } from 'src/views/Coversheet/utils/cardUtils';
import type { Section } from '../configurations/types';

interface CoverSheetContextInterface {
  section: Section;
  hiddenCards: Record<string, boolean>;
  toggleHiddenCard: (cardId: string) => void;
}

const CoverSheetSectionContext = createContext<CoverSheetContextInterface>({
  section: {} as Section,
  hiddenCards: {},
  toggleHiddenCard: () => null,
});

export function useCoverSheetSectionContext() {
  return useContext(CoverSheetSectionContext);
}

interface Props {
  children: ReactNode;
  section: Section;
}

export const CoverSheetSectionProvider = ({ children, section }: Props) => {
  const { getValues } = useFormContext();

  const [hiddenCards, setHiddenCards] = useState<Record<string, boolean>>(
    () => {
      const result: Record<string, boolean> = {};
      for (const card of section.cards) {
        const formCards = getValues(getCardFormDataPath(card));
        const entries = Array.isArray(formCards) ? formCards : [formCards];
        result[card.id] =
          !!card.hideByDefault &&
          // empty hideByDefault cards are inserted by default, filter them out
          entries.every((entity) => !entity?.id);
      }
      return result;
    },
  );

  const toggleHiddenCard = useCallback((cardId: string) => {
    setHiddenCards((hidden) => ({ ...hidden, [cardId]: !hidden[cardId] }));
  }, []);

  const contextValue = useMemo<CoverSheetContextInterface>(
    () => ({
      section,
      hiddenCards,
      toggleHiddenCard,
    }),
    [section, hiddenCards, toggleHiddenCard],
  );

  return (
    <CoverSheetSectionContext.Provider value={contextValue}>
      {children}
    </CoverSheetSectionContext.Provider>
  );
};
